/**
 * Apresentação de uma competência no modo "valor fixo com ajuste por horas"
 * (contrato HOURLY com hour_tracking_mode = FIXED_WITH_HOUR_ADJUSTMENT).
 *
 * O cronograma nasce no dia 1º e é recalculado a cada aprovação de horas, então no
 * meio do mês o valor esperado fica bem abaixo do fixo do contrato — a Joana tem
 * fixo de R$ 8.800 e vê R$ 5.200 enquanto faltam 72h de saldo. O número está
 * certo, mas exibi-lo como previsão de pagamento assusta sem informar nada que o
 * prestador possa usar. Enquanto dá para mexer no resultado, quem manda na tela é
 * o saldo de horas; o dinheiro vira apuração parcial.
 *
 * Nada aqui recalcula valor: a decomposição vem pronta do back.
 *
 * Três estados:
 *
 * - OPEN    — PENDING, sem NF e dentro do prazo de envio. Aprovar hora ainda muda
 *             o valor desta competência.
 * - FROZEN  — passou o prazo da NF (`is_frozen` do back). O back parou de
 *             recalcular e o saldo é acertado na competência seguinte, então o
 *             valor já é definitivo mesmo com o cronograma ainda pendente.
 * - SETTLED — pago ou com NF vinculada. Nada mais muda.
 */

/** 72 → '72,0h' */
export function formatHours(hours) {
  return `${Number(hours || 0).toFixed(1).replace(".", ",")}h`;
}

/** 'YYYY-MM-DD' → 'DD/MM' — o corte da janela, dito curto dentro da frase. */
function shortDate(dateStr) {
  if (!dateStr) return null;
  const [, month, day] = dateStr.split("-");
  return `${day}/${month}`;
}

/** Competência seguinte, 'MM/AAAA', virando o ano em dezembro. */
function nextCompetenceLabel(month, year) {
  const next = month === 12 ? { month: 1, year: year + 1 } : { month: month + 1, year };
  return `${String(next.month).padStart(2, "0")}/${next.year}`;
}

/**
 * Frase curta do saldo, na voz do que o prestador faz com ela.
 * O sinal já está no texto, então quem usa não precisa repetir + ou −.
 */
export function balanceHoursLabel(balanceHours) {
  const balance = Number(balanceHours) || 0;
  if (balance < 0) return `Faltam ${formatHours(Math.abs(balance))}`;
  if (balance > 0) return `${formatHours(balance)} a mais`;
  return "Horas em dia";
}

/**
 * A competência ainda pode mudar de valor sozinha?
 *
 * Vale para contrato por hora nos **dois** modos de apontamento, não só no de valor
 * fixo: o back recalcula o cronograma a cada aprovação de horas enquanto a
 * competência estiver aberta. O modo de valor fixo é só o único que tem
 * decomposição para mostrar na tela.
 *
 * Usado na planilha de remessa, onde a pergunta de quem paga não é "qual o modo do
 * contrato" e sim "esse valor ainda pode mudar".
 */
export function isCompetenceOpen(schedule) {
  return (
    schedule?.contract?.remuneration_type === "HOURLY" &&
    schedule?.status === "PENDING" &&
    !schedule?.tax_invoice_id &&
    !schedule?.is_frozen
  );
}

/**
 * Dados de apresentação da competência, ou null quando a tela não tem o que
 * mostrar de diferente — contrato fora do modo de valor fixo, ou resposta antiga
 * sem a decomposição.
 *
 * @param {object} schedule - Cronograma como vem da API
 * @param {{ start: string, end: string }|null} [period] - Janela de apuração, quando a tela já a calculou
 */
export function fixedAdjustmentCompetence(schedule, period = null) {
  if (!schedule) return null;
  // `expected_hours` só vem preenchido no modo de valor fixo com ajuste.
  if (schedule.expected_hours === null || schedule.expected_hours === undefined) {
    return null;
  }

  const settled = schedule.status !== "PENDING" || !!schedule.tax_invoice_id;
  const state = settled ? "SETTLED" : schedule.is_frozen ? "FROZEN" : "OPEN";

  const balanceHours = Number(schedule.balance_hours) || 0;
  const hourlyRate = parseFloat(
    schedule.hourly_rate ?? schedule.contract?.hourly_rate ?? 0
  );
  // O fixo já rateado pelos dias úteis de vigência: é nele que a competência
  // fecha se o saldo zerar, não no fixo cheio do contrato.
  const proratedFixed = parseFloat(schedule.prorated_fixed) || 0;

  return {
    state,
    isOpen: state === "OPEN",
    isFrozen: state === "FROZEN",
    expectedHours: Number(schedule.expected_hours) || 0,
    creditedHours: parseFloat(schedule.paid_hours) || 0,
    balanceHours,
    balanceLabel: balanceHoursLabel(balanceHours),
    balanceValue: Math.abs(balanceHours * hourlyRate),
    hourlyRate,
    proratedFixed,
    partialValue: parseFloat(schedule.expected_value) || 0,
    /** Valor da competência se o saldo zerar. */
    targetValue: proratedFixed,
    /** Último dia que ainda conta para esta competência, 'DD/MM'. */
    cutoffLabel: shortDate(period?.end || schedule.period_end),
    nextCompetence: nextCompetenceLabel(
      schedule.reference_month,
      schedule.reference_year
    ),
    isProrated:
      schedule.active_business_days < schedule.month_business_days,
  };
}
